/**
* ─────────────────────────────────────────────────────────────────────
* FILE: src/pages/SignIn.tsx
* DESCRIPTION: Custom sign-in page with VeriMark branding + 2FA support
* @copyright 2025-2026 Anou. All rights reserved.
*
* UPDATES:
* - 2026-06-08: Added second-factor authentication (2FA) handling
*               - Detects needs_second_factor from Clerk response
*               - Renders dynamic form for email_code/totp/backup_code
*               - Added resend code + back-to-password navigation
*               - Fixed TypeScript errors: updated to Clerk v5+ API methods
*                 (attemptSecondFactorVerification, prepareSecondFactorVerification)
*               - Preserved all existing branding and accessibility
* ─────────────────────────────────────────────────────────────────────
*/
import { useState } from 'react'
import { useSignIn } from '@clerk/react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, ArrowRight, ShieldCheck, RefreshCw, ArrowLeft } from 'lucide-react'

type SecondFactorStrategy = 'email_code' | 'totp' | 'backup_code' | 'phone_code'

export default function SignIn() {
  const { signIn } = useSignIn()
  const navigate = useNavigate()
  
  // Password form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false)
  const [secondFactorCode, setSecondFactorCode] = useState('')
  const [selectedStrategy, setSelectedStrategy] = useState<SecondFactorStrategy | null>(null)
  const [isResending, setIsResending] = useState(false)
  
  // Shared state
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    
    try {
      if (!signIn) {
        setError('Sign-in is not ready yet')
        return
      }

      const { error } = await signIn.password({
        identifier: email,
        password,
      })

      if (error) {
        setError(error.message || 'Invalid credentials')
        return
      }

      // Check if second factor is required
      if (signIn.status === 'needs_second_factor') {
        // Auto-select first available strategy for better UX
        const available = signIn.supportedSecondFactors?.[0]?.strategy as SecondFactorStrategy
        setSelectedStrategy(available || 'email_code')
        setNeeds2FA(true)
        return
      }

      // Complete sign-in if no 2FA required
      if (signIn.status === 'complete') {
        await signIn.finalize({
          navigate: () => navigate('/dashboard'),
        })
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Invalid credentials')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit2FA = async (e: React.FormEvent) => {
  e.preventDefault()
  setError('')
  setIsLoading(true)
  
  try {
    if (!signIn || !selectedStrategy) {
      setError('Authentication session expired')
      return
    }

    let error

    if (selectedStrategy === 'phone_code') {
      const result = await signIn.mfa.verifyPhoneCode({
        code: secondFactorCode,
      })
      error = result.error
    }

    if (selectedStrategy === 'totp') {
      const result = await signIn.mfa.verifyTOTP({
        code: secondFactorCode,
      })
      error = result.error
    }

    if (selectedStrategy === 'backup_code') {
      const result = await signIn.mfa.verifyBackupCode({
        code: secondFactorCode,
      })
      error = result.error
    }

    if (selectedStrategy === 'email_code') {
      setError('Email code is not supported as a second factor by this Clerk API')
      return
    }

    if (error) {
      setError(error.message || 'Invalid verification code')
      return
    }

    if (signIn.status === 'complete') {
      await signIn.finalize({
        navigate: () => navigate('/dashboard'),
      })
    }
  } catch (err: any) {
    setError(err.errors?.[0]?.message || 'Verification failed')
  } finally {
    setIsLoading(false)
  }
} 
   
  const handleResendCode = async () => {
    if (!signIn || !selectedStrategy) return
    setIsResending(true)
    setError('')
    
    try {
      if (selectedStrategy === 'phone_code') {
        await signIn.mfa.sendPhoneCode()
        setError('Verification code resent')
      }

      if (selectedStrategy !== 'phone_code') {
        setError('Resend is only available for phone verification codes')
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Failed to resend code')
    } finally {
      setIsResending(false)
    }
  }
  
  
  const handleBackToPassword = () => {
    setNeeds2FA(false)
    setSecondFactorCode('')
    setSelectedStrategy(null)
    setError('')
  }

  const handleSocialSignIn = async (strategy: 'oauth_google' | 'oauth_github') => {
    try {
      if (!signIn) {
        setError('Sign-in is not ready yet')
        return
      }
      const { error } = await signIn.sso({
        strategy,
        redirectCallbackUrl: '/sso-callback',
        redirectUrl: '/dashboard',
      })
      if (error) {
        setError(error.message || 'Social sign-in failed')
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Social sign-in failed')
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 2FA Form View
  // ─────────────────────────────────────────────────────────────
  if (needs2FA && selectedStrategy) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
              Two-factor authentication
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Enter the verification code sent to your {selectedStrategy === 'email_code' ? 'email' : selectedStrategy === 'totp' ? 'authenticator app' : 'phone'}
            </p>
          </div>

          {/* 2FA Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
            <form className="space-y-6" onSubmit={handleSubmit2FA}>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-start gap-2" role="alert">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p>{error}</p>
                    {error.toLowerCase().includes('password') && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        If you signed up with Google or GitHub, use that button instead.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={secondFactorCode}
                  onChange={(e) => setSecondFactorCode(e.target.value.replace(/\D/g, ''))}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center tracking-[0.5em] text-lg font-mono"
                  placeholder="••••••"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || secondFactorCode.length < 4}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 items-center gap-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Verify and sign in
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Actions */}
            <div className="mt-6 space-y-4">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className="w-full inline-flex justify-center items-center gap-2 py-2.5 px-4 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                {isResending ? 'Sending...' : 'Resend code'}
              </button>

              <button
                type="button"
                onClick={handleBackToPassword}
                className="w-full inline-flex justify-center items-center gap-2 py-2.5 px-4 text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to password
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────
  // Password Form View (Original)
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          {/* Real logo now */}
            <img 
              src="/logo.svg" 
              alt="Logo" 
              className="mx-auto h-16 w-auto object-contain mb-4 drop-shadow-sm" 
            />
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sign in to your account
          </p>
        {/* Sign Up Link */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Don&apos;t have an account?{' '}
          <Link to="/sign-up" className="font-medium text-blue-600 hover:text-blue-500">
            Sign up for free
          </Link>
        </p>

        </div>

        {/* Sign In Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
          <form className="space-y-6" onSubmit={handleSubmitPassword}>
            {error && !error.includes('resent') && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2" role="alert">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none relative block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 items-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or continue with</span>
            </div>
          </div>

          {/* Social Sign-In */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleSocialSignIn('oauth_google')}
              className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Google
            </button>
            <button
              type="button"
              onClick={() => handleSocialSignIn('oauth_github')}
              className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              GitHub
            </button>
          </div>
        </div>

        {/* Sign Up Link */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Don&apos;t have an account?{' '}
          <Link to="/sign-up" className="font-medium text-blue-600 hover:text-blue-500">
            Sign up for free
          </Link>
        </p>
      </div>
    </div>
  )
}